import { AddOns } from '@racemap/sdk/schema/billing/AddOns';
import {
  AppScreenKeys,
  AuthorizationStates,
  EventTypes,
  LeaderboardHeaderContent,
  TimingSystems,
  VisibilityStates,
} from '@racemap/sdk/schema/events';
import { DeviceClasses } from '@racemap/sdk/schema/trackers';
import type { PredictionParams } from '../types/events';
import { OneMinuteInMillis, OneSecondInMillis } from './time';

export { DeviceClasses, DeviceClassTypes } from '@racemap/sdk/schema/trackers';
export {
  EventTypes,
  AuthorizationStates,
  FlagContent,
  MapType,
  VisibilityStates,
  PinToShadowTrackMode,
  TimeMode,
  AppScreenKeys,
  TimingSystems,
  ModulesStates,
  LeaderboardType,
  LeaderboardHeaderContent,
  RegistrationType,
} from '@racemap/sdk/schema/events';

export const DefaultLogoUrl = 'https://static-cdn.racemap.com/img/default_logo.png';

export const RentGPSTrackerFormUrl = 'https://go.racemap.com/rent-gps';
export const RentGPSTrackerInfoUrl = 'https://go.racemap.com/gps-tracker#rent-pro';
export const HIGH_RESOLUTION_THRESHOLD = OneSecondInMillis * 10;
export const DEFAULT_POLLING_INTERVAL = 5000;
export const TEST_EVENT_NAME_PREFIX = 'AutoTestEvent';

/**
 * Age (in days) at which trash is considered safe to clean up. Used by the `only-old` list view,
 * the auto-cleanup job and the admin texts — it is NOT part of the base trash definition (an event
 * is trash regardless of age).
 */
export const TRASH_MIN_AGE_DAYS = 7;

/**
 * The size of a live data chunk in milliseconds.
 * Equivalent to 10 minutes (10 * 60,000ms = 600,000ms).
 */
export const LIVE_CHUNK_SIZE = 10 * OneMinuteInMillis;

/**
 * Fallback length of the net replay window. The player clamps its timeline to
 * `startTime + sliderDuration`, so this default is only safe for events that are
 * actually about two hours long — the editor seeds the real event duration instead.
 */
export const DEFAULT_NET_REPLAY_SLIDER_DURATION = 120 * OneMinuteInMillis;

export const AtomicEvents: Array<string> = [
  EventTypes.REGULAR,
  EventTypes.STAGE,
  EventTypes.CONTEST,
  EventTypes.SAFEMAP,
];

export const ParticipantAcceptingEventTypes: Array<EventTypes> = [
  EventTypes.CONTEST,
  EventTypes.REGULAR,
  EventTypes.SAFEMAP,
  EventTypes.STAGE_GROUP,
];

export const VisibilityLabels: Record<string, string> = {
  [VisibilityStates.DRAFT]: 'Draft',
  [VisibilityStates.UNLISTED]: 'Unlisted',
  [VisibilityStates.LISTED]: 'Listed',
  [VisibilityStates.PUBLIC]: 'Open',
  [VisibilityStates.ARCHIVED]: 'Archived',
};

export const AuthorizationLabels: Record<AuthorizationStates, string> = {
  [AuthorizationStates.NONE]: 'None',
  [AuthorizationStates.PAID]: 'Paid',
  [AuthorizationStates.PAID_FREE_LOADINGS]: 'Paid (with free loadings)',
  [AuthorizationStates.FREE]: 'Free',
};

export const TimingSystemLabels: Record<TimingSystems, string> = {
  [TimingSystems.NONE]: 'None',
  [TimingSystems.OTHER]: 'Other',
  [TimingSystems.FEIBOT]: 'Feibot',
  [TimingSystems.MY_LAPS]: 'MyLaps',
  [TimingSystems.RACERESULT]: 'RACE RESULT',
  [TimingSystems.CHRONO_TRACK]: 'ChronoTrack',
};

export enum TrackAvailibilityStates {
  WITH_TRACK = 'WITH_TRACK',
  WITHOUT_TRACK = 'WITHOUT_TRACK',
}

export const TrackAvailibilityLabels = {
  [TrackAvailibilityStates.WITH_TRACK]: 'With Predefined Track',
  [TrackAvailibilityStates.WITHOUT_TRACK]: 'Without Predefined Track',
};

export const TransponderClasses = [
  DeviceClasses.ChronoTrackTransponder,
  DeviceClasses.TrackPingTransponder,
  DeviceClasses.FeibotTransponder,
  DeviceClasses.MyLapsTransponder,
  DeviceClasses.Transponder,
];

export const DeviceClassLabels = {
  [DeviceClasses.Ghost]: 'Ghost',
  [DeviceClasses.Manual]: 'Manual',
  [DeviceClasses.Upload]: 'Upload',
  [DeviceClasses.Mobile]: 'Mobile',
  [DeviceClasses.Tracker]: 'Tracker',
  [DeviceClasses.TrackBox]: 'TrackBox',
  [DeviceClasses.Integration]: 'Integration',
  [DeviceClasses.Transponder]: 'Transponder',
  [DeviceClasses.MyLapsTransponder]: 'MyLapsTransponder',
  [DeviceClasses.FeibotTransponder]: 'FeibotTransponder',
  [DeviceClasses.TrackPingTransponder]: 'TrackPingTransponder',
  [DeviceClasses.ChronoTrackTransponder]: 'ChronoTrackTransponder',
  [DeviceClasses.HttpGpsTracker]: 'HTTP GPS Tracker',
};

export enum DeviceStateFlag {
  DISQUALIFIED = 'DSQ',
  DIDNOTSTART = 'DNS',
  DIDNOTFINISH = 'DNF',
  NORANK = '-',
}

export enum Targets {
  DISTANCE = 'DISTANCE',
  DURATION = 'DURATION',
}

export enum PlayerInfoSheetTabKeys {
  INFO = 'INFO',
  ELEVATION = 'ELEVATION',
  FLOW = 'FLOW',
  STATS = 'STATS',
  TIMING = 'TIMING',
}

export const PlayerInfoSheetTabLabels = {
  [PlayerInfoSheetTabKeys.INFO]: 'Event Infos',
  [PlayerInfoSheetTabKeys.ELEVATION]: 'Elevation Chart',
  [PlayerInfoSheetTabKeys.FLOW]: 'Event Flow',
  [PlayerInfoSheetTabKeys.STATS]: 'Statistics',
  [PlayerInfoSheetTabKeys.TIMING]: 'Leaderboard',
};

export const LeaderboardHeaderContentLabels = {
  [LeaderboardHeaderContent.NAME]: 'Name',
  [LeaderboardHeaderContent.ICON]: 'Icon',
  [LeaderboardHeaderContent.DISTANCE]: 'Distance',
};

export const AppScreenLabels = {
  [AppScreenKeys.PLAYER]: 'Map',
  [AppScreenKeys.LEADERBOARD]: 'Leaderboard',
};

export const AddOnLabels: Record<Exclude<AddOns, AddOns.MONITOR>, string> = {
  [AddOns.TIMING]: 'Leaderboard',
  [AddOns.TRACKER_RENTAL]: 'Tracker Rental',
  [AddOns.SPONSOR]: 'Sponsor Logo',
  [AddOns.API]: 'Data API',
  [AddOns.PREDICTIVE]: 'Predictive',
  [AddOns.KEYS]: 'Passcode & Keys',
  [AddOns.PARTICIPANTS]: 'Participants',
  [AddOns.HIGH_RESOLUTION]: 'High Resolution',
};

export const AddOnDocUrls: Record<Exclude<AddOns, AddOns.MONITOR>, string> = {
  [AddOns.TIMING]: 'https://docs.racemap.com/live-tracking/gps-timing',
  [AddOns.TRACKER_RENTAL]: 'https://docs.racemap.com',
  [AddOns.SPONSOR]: 'https://docs.racemap.com/live-tracking/map#sponsor-logo',
  [AddOns.API]: 'https://docs.racemap.com/api',
  [AddOns.PREDICTIVE]: 'https://docs.racemap.com/prediction',
  [AddOns.KEYS]: 'https://docs.racemap.com/apps/tracking/keys',
  [AddOns.PARTICIPANTS]: 'https://docs.racemap.com/api-import-and-export-data',
  [AddOns.HIGH_RESOLUTION]: 'https://docs.racemap.com/live-tracking/interpolation-interval',
};

export const numberOfActivationPeriod = 3;
export const unitOfActivationPeriod = 'month';

export enum PanelState {
  SUCCESS = 'success',
  WARNING = 'warning',
  DANGER = 'danger',
  PRIMARY = 'primary',
  NONE = 'secondary',
}

export const DefaultPredictionParams: PredictionParams = {
  // Parameter influencing the mapping
  // A reader location must be within this distance to the track
  distanceFromTrack: 50.0, // meters
  // Parameter influencing the interpolation allgorithm
  // the max speed in m/s that is used to correct faulty predictions in live mode
  maxCompensationSpeed: 100.0, // meters / second
  // minimum distance before we accept a new valid ping position -> bigger number decreases speed measuring errors
  minDistance: 60.0, // meters
  // all Pings below this value will be ignored
  rssiThreshold: -75.0, // dBm
  // At this point in future the simulated starter should meet the measured starter
  futureTrackScaleValue: 1.5,
  // We use this distance in meters to measure the speed of a starter for approximation
  lookBackDistance: 1000.0, // meters
  // we use this time interval to look into the future. In live mode we let the starters run lookForwardTimeInMs into the future. This should compensate for latency issues rising when pushing data through our system until it arrives in the player
  lookForwardTime: 15.0, // seconds
  // when the live prediction is to far away (timewise) from the last measured position we jump to the last measured position
  // we do a jump forward or backwards if the time difference is bigger than jumpingDotThresholdInMs
  jumpingDotThreshold: 360.0, // seconds
};

export const NULL_TAG = '--null--';
